// Helpers for WhatsApp Connect v2 (WP-06).
// Quality ratings, messaging limit tiers, name statuses, relative sync time, and token expiry.

import type { BadgeProps } from '../components/ui/Badge';

export interface QualityDisplay {
  labelKey: string;
  variant: NonNullable<BadgeProps['variant']>;
  dot: boolean;
}

export function getQualityRatingDisplay(rating: string | null | undefined): QualityDisplay {
  switch (rating?.toUpperCase()) {
    case 'GREEN':
      return { labelKey: 'connect.quality.GREEN', variant: 'success', dot: true };
    case 'YELLOW':
      return { labelKey: 'connect.quality.YELLOW', variant: 'warning', dot: true };
    case 'RED':
      return { labelKey: 'connect.quality.RED', variant: 'danger', dot: true };
    default:
      return { labelKey: 'connect.quality.UNKNOWN', variant: 'slate', dot: false };
  }
}

export function formatMessagingLimitTier(tier: string | null | undefined): { labelKey?: string; text?: string } {
  if (!tier) return { labelKey: 'connect.tier.unknown' };
  switch (tier) {
    case 'TIER_1K':
      return { labelKey: 'connect.tier.TIER_1K' };
    case 'TIER_10K':
      return { labelKey: 'connect.tier.TIER_10K' };
    case 'TIER_100K':
      return { labelKey: 'connect.tier.TIER_100K' };
    case 'TIER_UNLIMITED':
      return { labelKey: 'connect.tier.TIER_UNLIMITED' };
    default:
      return { text: tier.replace('TIER_', '') };
  }
}

export interface NameStatusDisplay {
  labelKey: string;
  variant: NonNullable<BadgeProps['variant']>;
}

export function getNameStatusDisplay(status: string | null | undefined): NameStatusDisplay {
  switch (status?.toUpperCase()) {
    case 'APPROVED':
      return { labelKey: 'connect.nameStatus.APPROVED', variant: 'success' };
    case 'AVAILABLE_WITHOUT_REVIEW':
      return { labelKey: 'connect.nameStatus.AVAILABLE_WITHOUT_REVIEW', variant: 'info' };
    case 'PENDING_REVIEW':
      return { labelKey: 'connect.nameStatus.PENDING_REVIEW', variant: 'warning' };
    case 'DECLINED':
      return { labelKey: 'connect.nameStatus.DECLINED', variant: 'danger' };
    case 'EXPIRED':
      return { labelKey: 'connect.nameStatus.EXPIRED', variant: 'slate' };
    default:
      return { labelKey: 'connect.nameStatus.AVAILABLE_WITHOUT_REVIEW', variant: 'neutral' };
  }
}

export function isTokenExpiringSoon(
  tokenExpiresAt: number | null | undefined,
  now = Date.now(),
  thresholdDays = 14,
): boolean {
  if (!tokenExpiresAt) return false;
  const thresholdMs = thresholdDays * 24 * 60 * 60 * 1000;
  return tokenExpiresAt < now + thresholdMs;
}

export function formatRelativeTime(
  timestamp: number | null | undefined,
  now = Date.now(),
): { key: string; params?: Record<string, string | number> } {
  if (!timestamp || timestamp <= 0) {
    return { key: 'connect.relative.never' };
  }
  const delta = Math.max(0, now - timestamp);
  const minutes = Math.floor(delta / 60000);
  if (minutes < 1) {
    return { key: 'connect.relative.justNow' };
  }
  if (minutes < 60) {
    return { key: 'connect.relative.minutesAgo', params: { n: minutes } };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { key: 'connect.relative.hoursAgo', params: { n: hours } };
  }
  const days = Math.floor(hours / 24);
  return { key: 'connect.relative.daysAgo', params: { n: days } };
}
